import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ArrowUpRight, Settings2, CheckCircle2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { billingApi } from '../../lib/billing';

/**
 * PlanHintBanner
 * Renders different plan-state surfaces on the Dashboard based on the user's
 * current subscription. The `position` prop lets the page render the banner
 * twice \u2014 once at the top, once at the bottom \u2014 with only the appropriate
 * variant showing in each slot:
 *   \u2022 `top`     \u2192 shows the small "ACTIVE PLAN" pill (gold/comp) and the
 *                 attention-grabbing "acquire" banner (none/expired) at the
 *                 top of the page.
 *   \u2022 `bottom`  \u2192 shows the soft "upgrade" pitch (one_time-active) only \u2014
 *                 a non-disruptive footer-style upsell.
 *
 * Renders null when there's nothing to show for the given slot.
 */
export function PlanHintBanner({ position = 'top' }) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    billingApi
      .me()
      .then((d) => {
        if (active) {
          setMe(d);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (active) setLoaded(true);
      });

    const refresh = () => {
      billingApi
        .me()
        .then((d) => {
          if (active) setMe(d);
        })
        .catch(() => {});
    };
    window.addEventListener('billing:refresh', refresh);
    return () => {
      active = false;
      window.removeEventListener('billing:refresh', refresh);
    };
  }, []);

  if (!loaded || !me) return null;

  const planType = me.plan_type || 'none';
  const status = me.status || 'inactive';
  const isComp = !!me.comp;

  // ------- Compact "manage plan" card for active ETL/Comp users -------
  // Top-only. These users have nothing to upsell; the small pill is a focused
  // entry point into the Billing page (where Cancel/Manage live).
  if (status === 'active' && (planType === 'gold' || planType === 'comp')) {
    if (position !== 'top') return null;
    const planName = planType === 'gold' ? 'Executive Thought Leadership' : 'Complimentary Access';
    return (
      <div
        className="mb-5 rounded-xl border border-border bg-card px-4 py-3 flex flex-wrap items-center justify-between gap-3"
        data-testid="plan-hint-banner"
        data-variant="manage"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <CheckCircle2 size={14} className="text-[hsl(var(--success))] shrink-0" />
          <span className="text-xs font-mono-edge tracking-[0.14em] text-muted-foreground">
            ACTIVE PLAN
          </span>
          <span className="text-sm font-medium truncate">{planName}</span>
          {isComp && (
            <Badge variant="secondary" className="font-mono-edge text-[10px] tracking-[0.14em]">
              COMP
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/billing')}
          className="text-xs h-8"
          data-testid="plan-hint-banner-manage-cta"
        >
          <Settings2 size={13} className="mr-1.5" />
          Manage plan
        </Button>
      </div>
    );
  }

  const isExpired = (planType === 'one_time' || planType === 'gold') && status !== 'active';
  const isOneTimeActive = planType === 'one_time' && status === 'active';

  // Variant: one_time-active users get an "Upgrade" pitch (still paying, but
  // lacking post-generation). Everyone else (none/expired) gets the
  // "pick a plan to run audits" pitch.
  const variant = isOneTimeActive ? 'upgrade' : 'acquire';

  // Slot routing: `upgrade` lives at the BOTTOM (soft upsell, non-blocking).
  // `acquire` lives at the TOP (user can't audit at all without a plan).
  if (variant === 'upgrade' && position !== 'bottom') return null;
  if (variant === 'acquire' && position !== 'top') return null;

  const copy = (() => {
    if (variant === 'upgrade') {
      return {
        badge: 'PROFILE SUCCESS',
        title: 'Ready for voice-matched posts? Upgrade to Executive Thought Leadership Plan.',
        body:
          'You\u2019re on the one-time Profile Success Plan. Upgrade to the Executive Thought Leadership Plan for unlimited audits, 40 posts/month, and 12 carousels/month \u2014 all with your voice baked in.',
        cta: 'Upgrade to Executive Thought Leadership',
        testid: 'plan-hint-banner-upgrade',
      };
    }
    return {
      badge: isExpired ? 'PLAN EXPIRED' : 'NO ACTIVE PLAN',
      title: isExpired
        ? 'Your plan has ended \u2014 pick one to run new audits.'
        : 'Pick a plan to run your first audit.',
      body:
        'You can still upload your profile and explore the wizard. We\u2019ll prompt you to upgrade right before the audit runs \u2014 no work lost.',
      cta: 'See pricing',
      testid: 'plan-hint-banner-acquire',
    };
  })();

  return (
    <div
      className="mb-5 rounded-2xl border border-primary/25 bg-gradient-to-br from-[hsl(var(--mint))]/40 via-card to-[hsl(var(--sky))]/30 p-4 sm:p-5"
      data-testid="plan-hint-banner"
      data-variant={variant}
    >
      <div className="flex flex-wrap items-start sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
            <Sparkles size={16} />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge
                variant="secondary"
                className="font-mono-edge text-[10px] tracking-[0.16em]"
                data-testid="plan-hint-banner-badge"
              >
                {copy.badge}
              </Badge>
            </div>
            <p className="font-semibold mt-1.5 text-sm sm:text-base">
              {copy.title}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
              {copy.body}
            </p>
          </div>
        </div>
        <Button
          onClick={() => navigate('/pricing')}
          className="rounded-xl shrink-0"
          data-testid="plan-hint-banner-cta"
        >
          {copy.cta} <ArrowUpRight size={14} className="ml-1.5" />
        </Button>
      </div>
      {variant === 'upgrade' && (
        <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground">
          <span>Need to cancel your one-time plan?</span>
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="underline underline-offset-4 hover:text-foreground transition-colors flex items-center gap-1"
            data-testid="plan-hint-banner-manage-link"
          >
            <Settings2 size={11} /> Manage plan
          </button>
        </div>
      )}
    </div>
  );
}

export default PlanHintBanner;
